<script setup lang="ts">
import { Badge, BadgeDot } from '~ds/components/ui/badge';
import { Button } from '~ds/components/ui/button';

import { useSignupLink } from '~/composables/unified/useSignupLink';
import { ENTRY_CTA, ENTRY_SOURCE } from '~/utils/unified/entryTracking';

const { buildLink } = useSignupLink();
const loginLink = computed(() => buildLink({ entrySource: ENTRY_SOURCE.ROOT_UNIFIED, entryCta: ENTRY_CTA.GET_LIFE_READY }));

const { t } = useI18n();

interface Skill {
  name: string;
  image: string;
};

const skills = computed<Skill[]>(() => [{
  name: t('unified.landing.getLifeReady.skills.managingMoney'),
  image: '/images/illustrations/piggy-bank.webp',
}, {
  name: t('unified.landing.getLifeReady.skills.gettingAJob'),
  image: '/images/illustrations/cream-2.webp',
}, {
  name: t('unified.landing.getLifeReady.skills.wellbeing'),
  image: '/images/illustrations/pixel-heart.webp',
}, {
  name: t('unified.landing.getLifeReady.skills.volunteering'),
  image: '/images/illustrations/clap.webp',
}, {
  name: t('unified.landing.getLifeReady.skills.sustainability'),
  image: '/images/illustrations/plant-in-soil.webp',
}, {
  name: t('unified.landing.getLifeReady.skills.digitalSkills'),
  image: '/images/illustrations/old-computer.webp',
}]);

const skillRefs = ref<HTMLElement[]>([]);
const showHighlight = ref<boolean[]>(skills.value.map(() => false));
const showIcon = ref<boolean[]>(skills.value.map(() => false));

let observer: IntersectionObserver | null = null;

onMounted(() => {
  if (typeof IntersectionObserver === 'undefined') {
    return;
  }

  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const index = Number((entry.target as HTMLElement).dataset.index);
      if (Number.isNaN(index)) {
        return;
      }
      if (entry.isIntersecting) {
        showHighlight.value[index] = true;
        showIcon.value[index] = true;
      }
      else {
        showHighlight.value[index] = false;
        showIcon.value[index] = false;
      }
    });
  }, {
    rootMargin: '-40% 0px -50% 0px',
    threshold: 0,
  });

  skillRefs.value.forEach(el => el && observer?.observe(el));
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});
</script>

<template>
  <div class="mb-20 md:mb-50 flex flex-col gap-6 md:gap-10 items-center">
    <Badge
      class="uppercase"
      tone="lime"
    >
      <BadgeDot />
      {{ t('unified.landing.getLifeReady.tag') }}
    </Badge>

    <div
      v-for="(skill, index) in skills"
      :ref="(el) => { if (el) skillRefs[index] = el as HTMLElement; }"
      :key="skill.name"
      :data-index="index"
      class="group relative"
    >
      <div
        class="absolute inset-0 z-0 transition-opacity duration-500 rounded-2xl bg-secondary blur-[12.2px]"
        :class="showHighlight[index] ? 'opacity-100' : 'opacity-0'"
      />
      <div class="relative text-3xl md:text-6xl font-semibold z-10 tracking-tight text-center">
        {{ skill.name }}
      </div>

      <div
        class="absolute transition-opacity duration-500 delay-200 top-1/4 left-0 group-even:right-0 group-even:left-auto"
        :class="showIcon[index] ? 'opacity-100' : 'opacity-0'"
      >
        <NuxtImg
          class="size-20 md:size-36 -translate-y-1/2 z-1 -translate-x-full group-even:translate-x-full z-20"
          :src="skill.image"
          alt=""
        />
      </div>
    </div>

    <Button
      size="lg"
      variant="outline"
      class="mt-10"
      as-child
    >
      <NuxtLink :to="loginLink">
        {{ $t('unified.landing.yourThing.cta') }}
      </NuxtLink>
    </Button>
  </div>
</template>
